import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, CheckCircle2, Circle, Clock, HelpCircle, Infinity as InfinityIcon, PenLine, ShieldAlert,
  ShieldCheck, XCircle, CalendarClock, FileText, Loader2, FlaskConical, Eye, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, openFile } from "../lib/api";

export function useApi(path, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      const r = await api.get(path);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => {
    load();
  }, [load]);
  return { data, error, loading, reload: load, setData };
}

const S = {
  not_started: ["Not started", Circle, "bg-slate-100 text-slate-800 border-slate-300"],
  in_progress: ["In progress", Clock, "bg-amber-100 text-amber-900 border-amber-300"],
  overdue: ["Overdue", AlertTriangle, "bg-rose-100 text-rose-900 border-rose-300"],
  completed: ["Complete", CheckCircle2, "bg-emerald-100 text-emerald-900 border-emerald-300"],
  cancelled: ["Cancelled", XCircle, "bg-slate-100 text-slate-600 border-slate-300"],
  submitted: ["Submitted", Clock, "bg-amber-100 text-amber-900 border-amber-300"],
  awaiting_review: ["Awaiting review", Clock, "bg-amber-100 text-amber-900 border-amber-300"],
  awaiting_signoff: ["Awaiting sign-off", Clock, "bg-sky-100 text-sky-900 border-sky-300"],
  accepted: ["Accepted", CheckCircle2, "bg-emerald-100 text-emerald-900 border-emerald-300"],
  approved: ["Approved", CheckCircle2, "bg-emerald-100 text-emerald-900 border-emerald-300"],
  needs_correction: ["Needs correction", PenLine, "bg-orange-100 text-orange-900 border-orange-300"],
  rejected: ["Rejected", XCircle, "bg-rose-100 text-rose-900 border-rose-300"],
  expired: ["Expired", ShieldAlert, "bg-rose-100 text-rose-900 border-rose-300"],
  expiring_soon: ["Expiring soon", CalendarClock, "bg-amber-100 text-amber-900 border-amber-300"],
  expiry_unknown: ["Expiry unknown", HelpCircle, "bg-slate-100 text-slate-800 border-slate-300"],
  no_expiry: ["No expiry", InfinityIcon, "bg-slate-100 text-slate-800 border-slate-300"],
  valid: ["Valid", ShieldCheck, "bg-emerald-100 text-emerald-900 border-emerald-300"],
  triaged: ["Triaged", Eye, "bg-sky-100 text-sky-900 border-sky-300"],
  resolved: ["Resolved", CheckCircle2, "bg-teal-100 text-teal-900 border-teal-300"],
  closed: ["Closed", Lock, "bg-slate-200 text-slate-800 border-slate-400"],
  draft: ["Draft", FileText, "bg-slate-100 text-slate-800 border-slate-300"],
  published: ["Published", CheckCircle2, "bg-emerald-100 text-emerald-900 border-emerald-300"],
  competent: ["Competent", CheckCircle2, "bg-emerald-100 text-emerald-900 border-emerald-300"],
  competent_with_restrictions: ["Competent with restrictions", AlertTriangle, "bg-amber-100 text-amber-900 border-amber-300"],
  not_yet_competent: ["Not yet competent", XCircle, "bg-rose-100 text-rose-900 border-rose-300"],
};

export function StatusChip({ status, label }) {
  const [text, Icon, cls] = S[status] || [status, HelpCircle, "bg-slate-100 text-slate-800 border-slate-300"];
  return (
    <span data-testid={`status-chip-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${cls}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {label || text}
    </span>
  );
}

export function SampleBadge({ show = true }) {
  if (!show) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded border border-dashed border-amber-500 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">
      <FlaskConical className="h-3 w-3" aria-hidden="true" /> Sample
    </span>
  );
}

export function PageHeader({ eyebrow, title, children, testid }) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up" data-testid={testid}>
      <div>
        {eyebrow && <p className="eyebrow text-[color:var(--dcbc-red)] mb-2">{eyebrow}</p>}
        <h1 className="text-3xl sm:text-4xl font-semibold text-[color:var(--ink)]">{title}</h1>
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function Panel({ title, action, children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`rounded-2xl border bg-card p-5 sm:p-6 fade-up ${className}`}>
      {(title || action) && (
        <div className="mb-4 flex items-center justify-between gap-3">
          {title && <h2 className="text-lg font-semibold">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Empty({ children, testid }) {
  return (
    <p data-testid={testid} className="rounded-xl border border-dashed bg-muted/40 p-5 text-sm text-muted-foreground">
      {children}
    </p>
  );
}

export function Loading() {
  return (
    <div className="flex items-center gap-2 p-6 text-muted-foreground" data-testid="loading">
      <Loader2 className="h-5 w-5 animate-spin" /> Loading…
    </div>
  );
}

export function ErrorNote({ error }) {
  if (!error) return null;
  return <p role="alert" className="rounded-xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">{error}</p>;
}

export function Field({ label, htmlFor, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-semibold">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export const inputCls =
  "w-full min-h-[48px] rounded-lg border border-input bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[color:var(--dcbc-red)]";

export function FileLinks({ files, testid = "file" }) {
  if (!files?.length) return <span className="text-sm text-muted-foreground">No file</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {files.map((f, i) => (
        <button key={f.id} type="button" data-testid={`${testid}-open-${i}`}
          onClick={() => openFile(f.id).catch((e) => toast.error(errMsg(e)))}
          className="inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-sm font-medium hover:border-[color:var(--dcbc-red)] transition-colors">
          <FileText className="h-4 w-4" aria-hidden="true" /> {f.filename}
        </button>
      ))}
    </div>
  );
}

export function Stat({ label, value, tone = "", testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 text-3xl font-semibold font-display ${tone}`}>{value}</p>
    </div>
  );
}

export const btn = {
  primary: "tap inline-flex items-center justify-center gap-2 rounded-lg bg-[color:var(--dcbc-red)] px-5 font-semibold text-white hover:bg-[#d91c2c] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50",
  dark: "tap inline-flex items-center justify-center gap-2 rounded-lg bg-[color:var(--ink)] px-5 font-semibold text-white hover:bg-black active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50",
  ghost: "tap inline-flex items-center justify-center gap-2 rounded-lg border bg-white px-4 font-semibold hover:bg-muted active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50",
};
